// Session touch: where this visit came from, for lead payloads (demo and trial).
// Consent-free because nothing is stored on the device: it is captured once from the
// first URL of the page load, held in memory only (composables/useSessionTouch.ts),
// and lost when the tab closes or reloads. It covers leads who land on one page and
// open a dialog on another, which the consent-gated w369_attr cookie can't when ad
// consent isn't granted. Pure: unit tested in tests/tracking/sessionTouch.test.ts.
import { buildTouch, stripClickIdsFromPath, type AdConsentValue } from './attributionCookie.ts';

export const SESSION_TOUCH_UTM_FIELDS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
] as const;

// The five UTM params the lead endpoints store as top-level fields.
export const LEAD_UTM_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

export type SessionTouch = {
  ts: number;
  landing_path: string;
  referrer_host: string;
  gclid: string;
  gbraid: string;
  wbraid: string;
  fbclid: string;
} & Record<(typeof SESSION_TOUCH_UTM_FIELDS)[number], string>;

export type LeadUtm = Record<(typeof LEAD_UTM_FIELDS)[number], string>;

// Text and click ids get buildTouch's own cleaning and clipping (a dirty click id is
// dropped, gclid wins over gbraid/wbraid, text is cut at 200 chars). landing_path keeps the query, so the page's
// own params are visible, but never a click id (stripClickIdsFromPath).
export function buildSessionTouch(url: URL, referrer: string, nowMs: number): SessionTouch {
  const touch = buildTouch(url, referrer, nowMs);
  const session = {
    ts: nowMs,
    landing_path: stripClickIdsFromPath(`${url.pathname}${url.search}`).slice(0, 500),
    referrer_host: touch.referrer_host,
    gclid: touch.gclid,
    gbraid: touch.gbraid,
    wbraid: touch.wbraid,
    fbclid: touch.fbclid,
  } as SessionTouch;
  for (const field of SESSION_TOUCH_UTM_FIELDS) session[field] = touch[field];
  return session;
}

// Click ids leave the browser only with granted ad consent (spec I1, the same rule
// useAttribution applies to source_path); the rest of the touch is always sent.
export function sessionTouchForLead(touch: SessionTouch | null, adConsent: AdConsentValue): SessionTouch | null {
  if (!touch) return null;
  return adConsent === 'granted' ? { ...touch } : { ...touch, gclid: '', gbraid: '', wbraid: '', fbclid: '' };
}

// The UTMs a lead reports: the current page's when its URL carries any, otherwise
// the ones this visit landed with. The five always come from one URL, never mixed.
export function leadUtmFields(currentSearch: string, touch: SessionTouch | null): LeadUtm {
  const params = new URLSearchParams(currentSearch);
  const current = {} as LeadUtm;
  for (const field of LEAD_UTM_FIELDS) current[field] = (params.get(field) || '').slice(0, 200);
  if (LEAD_UTM_FIELDS.some((field) => current[field]) || !touch) return current;
  const landed = {} as LeadUtm;
  for (const field of LEAD_UTM_FIELDS) landed[field] = touch[field];
  return landed;
}
